import type { FollowUpCreditClaim } from "@/features/follow-up/types";
import type { Contact } from "@/features/contact/types";
import type { OutcomeReason } from "@/types/outcome-reason";
import type { Doctor } from "./doctor";
import type { DoctorTimeOff } from "./doctor-time-off";
import type { Treatment } from "./treatment";

export const PATIENT_GENDER = {
  MALE: "Male",
  FEMALE: "Female",
} as const;

export type PatientGender =
  (typeof PATIENT_GENDER)[keyof typeof PATIENT_GENDER];

export const APPOINTMENT_STATUS = {
  PENDING: "pending",
  CONFIRMED: "confirmed",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
  NO_SHOW: "no_show",
} as const;

export type AppointmentStatus =
  (typeof APPOINTMENT_STATUS)[keyof typeof APPOINTMENT_STATUS];
export const APPOINTMENT_STATUSES = Object.values(
  APPOINTMENT_STATUS,
) as Array<AppointmentStatus>;

export const APPOINTMENT_STATUS_COLORS: Record<AppointmentStatus, string> = {
  pending: "#3b82f6",
  confirmed: "#10b981",
  completed: "#64748b",
  cancelled: "#f43f5e",
  no_show: "#f59e0b",
};

export type AppointmentDoctor = Pick<
  Doctor,
  "id" | "name" | "photo" | "externalId"
>;

export type AppointmentContact = Pick<
  Contact,
  // The assignee is whose to-do a blocked appointment is: the sweep's
  // responsible ladder assigns the contact, not the appointment.
  | "id"
  | "firstName"
  | "lastName"
  | "phone"
  | "email"
  | "imageUrl"
  | "assignedUserId"
  // Where the patient came from - the dashboard's appointments table filters on it.
  | "origin"
>;

export type AppointmentTreatment = Pick<Treatment, "id" | "name">;

export type AppointmentPatient = {
  firstName: string;
  lastName: string;
  phone: string;
  gender?: PatientGender;
  contactId?: string;
};

export type Appointment = {
  id: string;
  startDate: string;
  endDate: string;
  doctorId: string;
  doctor: AppointmentDoctor;
  contactId: string | null;
  contact?: AppointmentContact | null;
  treatmentId: string | null;
  treatment: AppointmentTreatment | null;
  /** Chosen at booking time. Null means the reports derive it from the treatment or doctor. */
  departmentId?: string | null;
  /**
   * Booked for a contact the viewer's role can't see. It still holds its time
   * with its doctor, so it shows as blocked time, with nothing about the
   * patient, and doesn't open.
   */
  isHidden?: boolean;
  patients?: AppointmentPatient[];
  status: AppointmentStatus;
  notes: string | null;
  cancellationReason?: string | null;
  color?: string;
  organizationId: string;
  workspaceId: string;
  createdAt: string;
  updatedAt: string;
  externalId?: string | null;
  externalData?: AppointmentExternalData | null;
  activities?: AppointmentActivity[];
  bookedBy?: AppointmentBookedBy;
  bookedByUserId?: string | null;
  /**
   * Read with the two above: `ai` and no user is the assistant acting alone,
   * `ai` with a user is the assistant proposing and that person approving, and
   * `user` with this flag is a person booking with its help.
   */
  isAiAssisted?: boolean;
  /**
   * First-touch ad-click attribution, stamped by the list endpoint - a fact
   * about the contact, not this booking. Null means an organic patient.
   */
  campaignId?: string | null;
  campaignName?: string | null;
  rescheduledCount?: number;
  /**
   * Admin-only, and absent rather than null for everyone else - the API drops
   * it from the payload unless the requester is an admin. `null` therefore
   * means "an admin is looking and nobody has priced this yet"; `undefined`
   * means "not an admin, no opinion available". Code that renders a
   * missing-value state has to check the first without mistaking it for the
   * second.
   */
  value?: string | null;
  /**
   * Whether this is the patient's first visit or a follow-up, as recorded.
   * ClinicSoft sends it; a booking made here sets it from the form.
   */
  visitType?: VisitType | null;
  /**
   * Free consultation or paid service, taken from the treatment at booking time
   * and editable after. ClinicSoft-originated appointments are always `service`
   * - it sends no treatment, so there is nothing to classify them from.
   */
  bookingType: BookingType;
  /**
   * Whether the visit is chargeable. Null means nobody has said yet - a
   * CRM-booked visit is answered from its ledger rather than guessed at.
   */
  isPaid: boolean | null;
  /** Set on any attended visit - what came of it. */
  outcome?: AppointmentOutcome | null;
  /** The outcome reason an ending answer named. Detail endpoint only. */
  outcomeReason?: Pick<OutcomeReason, "id" | "label"> | null;
  /** The free consultation this appointment was booked out of, if any. */
  originAppointmentId?: string | null;
  /**
   * Both ends of the consultation-to-service link. Only the detail endpoint
   * returns them - a list has nowhere to put them - so treat them as absent
   * anywhere the appointment came from a list query.
   */
  originAppointment?: LinkedAppointment | null;
  convertedAppointments?: LinkedAppointment[];
  /**
   * The open question a human owes this row, stored server-side and reset by
   * whichever write answers it. One reason at a time.
   */
  isResolveBlocked: boolean;
  resolveBlockedReason?: ResolveBlockedReason | null;
  /**
   * Which step of being closed out this row is on. Absent on rows served by an
   * older API, which is why every read of it goes through `resolutionStatusOf`.
   */
  resolutionStatus?: ResolutionStatus | null;
  /** For `waiting_on_user`: when the patient asked to be chased again. */
  waitingOnUserTill?: string | null;
  /** When the escalation email for the current block went out, if it has. */
  resolveBlockEscalatedAt?: string | null;
  /**
   * The advance this visit was drawn from, when the agent said so. The amount
   * only lands once the CRM sends the allocation receipt.
   */
  advanceId?: string | null;
  /** When the outcome flow last pulled this visit's billing from the CRM, or never. */
  billingCheckedAt?: string | null;
  /**
   * The contact's nearest upcoming booking after this one. Detail endpoint
   * only, like `originAppointment`.
   */
  nextAppointment?: LinkedAppointment | null;
};

/**
 * The other half of a consultation-to-service pair - enough to name it and
 * price it, never the whole appointment.
 */
export type LinkedAppointment = {
  id: string;
  startDate: string;
  status: AppointmentStatus;
  bookingType: BookingType;
  treatment?: { id: string; name: string } | null;
  /** Admin-only, on the same terms as `value`: absent means "not allowed". */
  value?: string | null;
};

export type AppointmentSlot = {
  startDate: Date;
  endDate: Date;
  resourceId?: string;
  bounds?: { x: number; y: number };
};

export type AppointmentViewMode = "calendar" | "list";

export type CreateAppointmentPayload = Pick<
  Appointment,
  | "startDate"
  | "endDate"
  | "contactId"
  | "doctorId"
  | "treatmentId"
  | "departmentId"
  | "bookedByUserId"
  | "patients"
  | "status"
  | "notes"
  | "visitType"
  | "cancellationReason"
> & {
  /** Defaults to `service` when the caller does not say. */
  bookingType?: BookingType;
  /** Asked when the slot is booked; omitted leaves it unknown. */
  isPaid?: boolean | null;
  originAppointmentId?: string | null;
  /**
   * The follow-up the form offered and whether it was left ticked. Omitted,
   * the system decides by timing.
   */
  creditFollowUp?: FollowUpCreditClaim | null;
};

export type UpdateAppointmentPayload = Partial<CreateAppointmentPayload>;

export const APPOINTMENT_CRM_STATUS = {
  NOT_SYNCED: "not-synced",
  SYNCED: "synced",
} as const;

export type AppointmentCrmStatus =
  (typeof APPOINTMENT_CRM_STATUS)[keyof typeof APPOINTMENT_CRM_STATUS];

export type AppointmentFilter = {
  doctorIds?: string[];
  startDate?: string;
  endDate?: string;
  statuses?: AppointmentStatus[];
  contactId?: string;
  bookedBy?: AppointmentBookedBy[];
  bookedByUserIds?: string[];
  /** The blocked worklist, OR-ed across the reasons given. */
  blockedReasons?: ResolveBlockedReason[];
  /** Upcoming bookings with no treatment, or no department anything can tell. */
  missingDetails?: boolean;
  /** `not-synced`: booked here but refused by the CRM, so its schedule never
   *  got them (the "not in the CRM" worklist). `synced`: the CRM holds it. */
  crmStatus?: AppointmentCrmStatus;
};

/** `page` is 1-based; `limit` is the page size (the API caps it at 100). */
export type AppointmentPageParams = {
  page: number;
  limit: number;
};

/** Every open question in the workspace, grouped by reason. */
export type AppointmentBlockedCounts = {
  staleStatus: number;
  paymentMissing: number;
  waitingOnUser: number;
  missingDetails: number;
  crmPushFailed: number;
};

/**
 * The same split again per creator bucket, in one response. Both segments on
 * the worklist read from this: the tabs are a cross-product, so fetching them
 * separately would let one lag behind the other mid-toggle.
 */
export type AppointmentBlockedCountsByCreator = AppointmentBlockedCounts & {
  mine: AppointmentBlockedCounts;
  ai: AppointmentBlockedCounts;
};

/**
 * The whole answer to "what happened at this appointment", in one request.
 *
 * The client decides which steps to show and posts once at the end. A
 * half-applied outcome - a visit marked attended with the money never recorded
 * - is worse than a failed one, so nothing is sent until the flow is finished.
 */
export type CreateAppointmentOutcomePayload = {
  attendance: AttendanceAnswer;
  /** Required when `attendance` is `no_show`. */
  noShowNext?: NoShowNextStep;
  /** `rescheduled` only - the new booking's slot, not a move of this one. */
  startDate?: string;
  endDate?: string;
  /**
   * What the visit itself cost. Omitted when it was free.
   *
   * Recorded against the appointment being resolved, not against anything
   * booked below - this is the money that changed hands for the visit that just
   * happened.
   */
  /** `false` is the "it was free" answer and banks nothing. */
  isPaid?: boolean;
  payment?: { amount: number; notes?: string };
  /** No CRM: taken off the contact's latest advance. At most one of the three money fields. */
  advanceDraw?: { amount: number };
  /** No CRM: a deposit paid today, banked on the contact for later visits. */
  newAdvance?: { amount: number };
  /** The visit was drawn from the contact's advance; the CRM prices it later. */
  usedAdvance?: boolean;
  /**
   * An upcoming booking that already answers "coming again?" or "rescheduled
   * to?". Linked as the follow-on instead of creating one.
   */
  nextAppointmentId?: string;
  /** Asked only when the booking never said and nothing can tell. */
  departmentId?: string;
  /** Asked only of a booking that never said what it was for. */
  treatmentId?: string;
  /** `showed_up` - required on a free consultation, optional otherwise. */
  consultationAnswer?: ConsultationAnswer;
  serviceAppointment?: {
    doctorId: string;
    treatmentId?: string | null;
    startDate: string;
    endDate: string;
    visitType?: VisitType | null;
    notes?: string;
    /** Whether the new booking is chargeable; the amount is settled later. */
    isPaid?: boolean;
    /** Defaults to `service`; a free consultation is asked its outcome afterwards. */
    bookingType?: BookingType;
  };
  /** `still_deciding` - the date the patient asked for. Must be in the future. */
  waitingOnUserTill?: string;
  /**
   * `nothing_booked` - when to check back; `all_done` - the recall date.
   * Omitted leaves the timing to the workflow.
   */
  followUpAt?: string;
  followUpReason?: string;
  /**
   * `not_interested`, `nothing_booked`, `all_done` and `wont_come` - the
   * outcome reason that names why. Required for `all_done` on a consultation.
   */
  outcomeReasonId?: string;
};

export const BOOKING_TYPE = {
  CONSULTATION: "consultation",
  SERVICE: "service",
} as const;

export type BookingType = (typeof BOOKING_TYPE)[keyof typeof BOOKING_TYPE];

/**
 * What came of an attended visit, as stored.
 * "Still deciding" is not a value here: it lives on the resolve-block columns.
 */
export const APPOINTMENT_OUTCOME = {
  BOOKED_SERVICE: "booked_service",
  /** They came, and no booking came of it - a plain non-conversion, no refusal stated. */
  NOTHING_BOOKED: "nothing_booked",
  NOT_INTERESTED: "not_interested",
  STOPPED_RESPONDING: "stopped_responding",
  /** The visit was all they needed - no next visit expected, so nothing to chase. */
  ALL_DONE: "all_done",
} as const;

export type AppointmentOutcome =
  (typeof APPOINTMENT_OUTCOME)[keyof typeof APPOINTMENT_OUTCOME];

/**
 * The six answers the outcome form can send. Not the stored enum:
 * `still_deciding` writes a waiting block rather than an outcome.
 */
export const CONSULTATION_ANSWER = {
  BOOKED_SERVICE: "booked_service",
  STILL_DECIDING: "still_deciding",
  NOTHING_BOOKED: "nothing_booked",
  NOT_INTERESTED: "not_interested",
  STOPPED_RESPONDING: "stopped_responding",
  ALL_DONE: "all_done",
} as const;

export type ConsultationAnswer =
  (typeof CONSULTATION_ANSWER)[keyof typeof CONSULTATION_ANSWER];

/** The open question a human owes an appointment, stored on the row. */
export const RESOLVE_BLOCKED_REASON = {
  STALE_STATUS: "stale_status",
  PAYMENT_MISSING: "payment_missing",
  WAITING_ON_USER: "waiting_on_user",
} as const;

export type ResolveBlockedReason =
  (typeof RESOLVE_BLOCKED_REASON)[keyof typeof RESOLVE_BLOCKED_REASON];

/**
 * How far through being closed out an appointment is.
 *
 * Answers what `isResolveBlocked: false` could not - it means both "nothing
 * owed yet" and "everything answered", so a quiet row gave no way of telling a
 * finished visit from one whose slot has not come round. Declared in walk
 * order; a reschedule puts a row back to `NOT_DUE`.
 */
export const RESOLUTION_STATUS = {
  NOT_DUE: "not_due",
  AWAITING_ATTENDANCE: "awaiting_attendance",
  AWAITING_PAYMENT: "awaiting_payment",
  AWAITING_DECISION: "awaiting_decision",
  SETTLED: "settled",
} as const;

export type ResolutionStatus =
  (typeof RESOLUTION_STATUS)[keyof typeof RESOLUTION_STATUS];

/** The two answers to "did they turn up?". A no-show then owes a next step. */
export const ATTENDANCE_ANSWER = {
  SHOWED_UP: "showed_up",
  NO_SHOW: "no_show",
} as const;

export type AttendanceAnswer =
  (typeof ATTENDANCE_ANSWER)[keyof typeof ATTENDANCE_ANSWER];

/**
 * Where a no-show goes next. Missing a slot is not an ending, and the clinic's
 * next move differs in each of these - which is why rebooking is a child of
 * "they didn't turn up" rather than an alternative to it.
 */
export const NO_SHOW_NEXT_STEP = {
  WONT_COME: "wont_come",
  RESCHEDULED: "rescheduled",
  WILL_TELL_LATER: "will_tell_later",
} as const;

export type NoShowNextStep =
  (typeof NO_SHOW_NEXT_STEP)[keyof typeof NO_SHOW_NEXT_STEP];

export const VISIT_TYPE = {
  FIRST_TIME: "first_time",
  FOLLOW_UP: "follow_up",
  REPEAT: "repeat",
} as const;

export type VisitType = (typeof VISIT_TYPE)[keyof typeof VISIT_TYPE];

export const VISIT_TYPE_LABEL: Record<VisitType, string> = {
  first_time: "First visit",
  follow_up: "Follow-up",
  repeat: "Repeat visit",
};

export const VISIT_TYPE_OPTIONS = Object.values(VISIT_TYPE).map((value) => ({
  value,
  label: VISIT_TYPE_LABEL[value],
}));

export const BOOKING_TYPE_LABEL: Record<BookingType, string> = {
  consultation: "Free consultation",
  service: "Service",
};

export const BOOKING_TYPE_OPTIONS = Object.values(BOOKING_TYPE).map(
  (value) => ({ value, label: BOOKING_TYPE_LABEL[value] }),
);

/**
 * The words an agent taps in the inbox, verbatim. They are also the chip in the
 * appointments list and the buckets in the funnel report - one vocabulary end to
 * end, so nobody has to work out that "booked_service" is the same thing.
 */
export const APPOINTMENT_OUTCOME_LABEL: Record<AppointmentOutcome, string> = {
  booked_service: "Booked a service",
  nothing_booked: "Nothing booked",
  not_interested: "Not interested",
  stopped_responding: "No response",
  all_done: "All done",
};

export const RESOLVE_BLOCKED_REASON_LABEL: Record<
  ResolveBlockedReason,
  string
> = {
  stale_status: "Did they turn up?",
  payment_missing: "Needs a payment",
  waiting_on_user: "Did they decide?",
};

// For react-big-calendar event format
type CalendarEventBase = {
  id: string;
  title: string;
  start: Date;
  end: Date;
  resourceId: string;
};

/**
 * The calendar draws two different things, and they are not variations on each
 * other: an appointment is somebody booked in, a block is the absence of
 * availability. Discriminating on `kind` rather than making `appointment`
 * optional means every read of `event.appointment` has to prove which one it is
 * holding, which is what stops a block being dragged, resized, or opened as a
 * patient record.
 */
export type CalendarAppointmentEvent = CalendarEventBase & {
  kind: "appointment";
  appointment: Appointment;
};

export type CalendarBlockEvent = CalendarEventBase & {
  kind: "block";
  block: DoctorTimeOff;
};

export type CalendarEvent = CalendarAppointmentEvent | CalendarBlockEvent;

export const isAppointmentEvent = (
  event: CalendarEvent,
): event is CalendarAppointmentEvent => event.kind === "appointment";

export const isBlockEvent = (
  event: CalendarEvent,
): event is CalendarBlockEvent => event.kind === "block";

// For react-big-calendar resource format
export type CalendarResource = {
  id: string;
  title: string;
  photo?: string | null;
  type?: "doctor" | "day";
  date?: Date;
};

export type CalendarView = "day" | "week" | "month";

// Activity types
export const APPOINTMENT_ACTIVITY_ACTION = {
  CREATE: "create",
  PATIENT_UPDATE: "patient_update",
  TREATMENT_UPDATE: "treatment_update",
  DOCTOR_UPDATE: "doctor_update",
  STATUS_UPDATE: "status_update",
  SCHEDULE_UPDATE: "schedule_update",
  DELETE: "delete",
  BOOKED_BY_UPDATE: "booked_by_update",
  VALUE_UPDATE: "value_update",
  CRM_LINK: "crm_link",
} as const;

export type AppointmentActivityAction =
  (typeof APPOINTMENT_ACTIVITY_ACTION)[keyof typeof APPOINTMENT_ACTIVITY_ACTION];

export const APPOINTMENT_BOOKED_BY = {
  USER: "user",
  CRM: "crm",
  AI: "ai",
} as const;

export type AppointmentBookedBy =
  (typeof APPOINTMENT_BOOKED_BY)[keyof typeof APPOINTMENT_BOOKED_BY];

export type AppointmentActivity = {
  userId: string | null;
  integrationId: string | null;
  action: AppointmentActivityAction;
  timestamp: string;
  payload?: { previous?: unknown; new?: unknown };
};

// CRM (ClinicSoft) types -------------------------------------------------

export type CrmDoctorScheduleSlot = {
  slotId: number;
  availableTime: string;
};

export type CrmDoctorScheduleDay = {
  date: string;
  slots: CrmDoctorScheduleSlot[];
};

export type CrmDoctorScheduleResponse = {
  doctorLicense: string;
  doctorName?: string;
  availability: CrmDoctorScheduleDay[];
};

export type AppointmentExternalRaw = {
  VisitId?: number;
  message?: string;
  [key: string]: unknown;
};

/** What a push to the CRM was doing. */
export type CrmSyncAction = "create" | "update" | "delete" | "sync";

export type AppointmentExternalError = {
  code?: string;
  action?: CrmSyncAction;
  details?: string;
  message: string;
  occurredAt: string;
};

export type AppointmentExternalDrift = {
  message: string;
  occurredAt: string;
  orphanedVisitId?: string;
  orphanedEnquiryId?: string;
};

export type AppointmentExternalData = {
  appointmentId?: string;
  patientId?: string;
  enquiryId?: string;
  integrationId?: string;
  fromSlot?: number;
  toSlot?: number;
  fromTime?: string;
  toTime?: string;
  isDrifted?: boolean;
  raw?: AppointmentExternalRaw | null;
  error?: AppointmentExternalError | null;
  drift?: AppointmentExternalDrift | null;
};

export type SyncDoctorsFromCrmResult = {
  fetched: number;
  created: number;
  updated: number;
  skipped: number;
};

/**
 * Which fact takes an `AppointmentRow`'s title line. Every surface is
 * organised by a different one - the contact panel by the doctor, the queue by
 * the patient, Today by the clock - so the lead is chosen per surface and the
 * facts it does not take fall through to the last line.
 */
export const APPOINTMENT_ROW_LEAD = {
  TREATMENT: "treatment",
  DOCTOR: "doctor",
  PERSON: "person",
  TIME: "time",
} as const;

export type AppointmentRowLead =
  (typeof APPOINTMENT_ROW_LEAD)[keyof typeof APPOINTMENT_ROW_LEAD];

/**
 * Anything an appointment row can be handed. Structural on purpose: a full
 * `Appointment`, a `LinkedAppointment` (no end time, no doctor) and the agent
 * preview's own appointment are three shapes of the same thing, and the row
 * reads whichever fields are there rather than making each call site translate.
 */
export interface AppointmentLike {
  startDate: string;
  endDate?: string | null;
  status?: AppointmentStatus | null;
  bookingType?: BookingType;
  treatment?: { name: string } | null;
  /** The agent preview carries names rather than records. */
  treatmentName?: string | null;
  doctor?: { name: string } | null;
  doctorName?: string | null;
  contact?: {
    firstName?: string | null;
    lastName?: string | null;
    phone?: string | null;
  } | null;
  patients?: {
    firstName?: string | null;
    lastName?: string | null;
    phone?: string | null;
  }[];
}

export interface AppointmentRowLinesInput {
  appointment: AppointmentLike;
  lead: AppointmentRowLead;
  /** Resolved by the caller, which holds the departments list. */
  department?: string | null;
  /** Anything the surface owes the reader after the facts, e.g. "Asked for time until Sep 1". */
  note?: string | null;
}

/** Three lines, named for where they sit rather than what they hold - what a line holds depends on the lead. */
export interface AppointmentRowLines {
  title: string;
  secondary: string;
  tertiary: string;
}
